import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useKeycloak } from "keycloak-react-web";

import { isPermissionsEnabled } from "../../app/utils";
import { handleGetMyPermissions } from "./permissionsSlice";
import { clearProfile, handleGetMyProfile } from "../profile/profileSlice";

/**
 * Owns the signed-in account fetches. Called once, from `App`, so readers do not issue duplicates.
 *
 * Re-fetched on token refresh rather than only on mount: a role granted while somebody is signed in
 * reaches their token at the next refresh, and a menu that only updated on a full reload would make
 * "I granted it and nothing happened" the most common support question once roles can be changed live.
 *
 * Profile refresh lives here too because keycloak-js exposes one refresh callback. Two independent
 * hooks would overwrite one another or restore a stale callback during cleanup.
 *
 * Kept apart from `usePermissions` because `keycloak-react-web` renders on import, which throws in
 * any test that has not mocked it. Every gated button reads `usePermissions`, so if the reader
 * imported this file, adding a gate to a component would break that component's existing tests for
 * a reason with nothing to do with permissions.
 */
export const usePermissionsSync = () => {
  const dispatch = useDispatch();
  const { keycloak, initialized } = useKeycloak();
  const enabled = isPermissionsEnabled();

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated) {
      dispatch(clearProfile());
      return undefined;
    }

    const refreshSessionData = () => {
      if (enabled) dispatch(handleGetMyPermissions());
      dispatch(handleGetMyProfile());
    };

    refreshSessionData();

    // Chained rather than assigned: keycloak-js exposes single-handler callbacks, and overwriting
    // one somebody else set is a silent regression in whatever depended on it.
    const previous = keycloak.onAuthRefreshSuccess;
    keycloak.onAuthRefreshSuccess = () => {
      if (typeof previous === "function") previous();
      refreshSessionData();
    };

    return () => {
      keycloak.onAuthRefreshSuccess = previous;
    };
  }, [dispatch, enabled, initialized, keycloak]);
};
